import React, { useEffect, useState } from "react";
import Breadcrumb from "../../components/Breadcrumb";
import { Button, Input, Modal, Popconfirm, Table } from "antd";
import {
  // CheckCircleOutlined,
  // CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  ADD_AMENITY,
  UPDATE_AMENITY,
  GET_ALL_COMPANY_AMENITIES,
} from "../../graphql/companyQueries";
import { useMutation, useQuery } from "@apollo/client";
import Toast from "../../components/Toast/Toast";
import { useParams } from "react-router-dom";
import {
  EmptyState,
  ErrorBanner,
  ErrorState,
  skeletonTableProps,
} from "../../components/states";
import { useToast } from "../../components/Toast/toastHook";
import useDebounce from "../../hooks/useDebounce";

const Amenities = (props) => {
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalCount: 0,
  });
  const { companyId } = useParams();
  const [open, setOpen] = useState(false);
  const [selectedAmenity, setSelectedAmenity] = useState("");
  const [selectAmenityId, setSelectAmenityId] = useState(null);
  const [viewMode, setViewMode] = useState(null);
  // const [currentAmenity, setCurrentAmenity] = useState(null);

  const [searchText, setSearchText] = useState("");
  const debounceValue = useDebounce(searchText, 500);

  const [amenities, setAmenities] = useState([]);

  const toast = useToast(4000);

  const handleOk = () => {
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
    setViewMode(null);
  };

  const showModal = () => {
    setOpen(true);
  };

  // const checkIsActive = (record) => {
  //   if (record.isActive) {
  //     return (
  //       <div style={{ display: "flex", justifyContent: "center" }}>
  //         <CheckCircleOutlined style={{ fontSize: "20px", color: "green" }} />
  //       </div>
  //     );
  //   } else {
  //     return (
  //       <div style={{ display: "flex", justifyContent: "center" }}>
  //         <CloseCircleOutlined style={{ fontSize: "20px", color: "red" }} />
  //       </div>
  //     );
  //   }
  // };

  const { loading, error, data, refetch } = useQuery(
    GET_ALL_COMPANY_AMENITIES,
    {
      variables: {
        companyId,
        pagination: {
          page: pagination.page,
          limit: pagination.limit,
        },
        searchText: debounceValue,
      },
      fetchPolicy: "no-cache",
      onError(err) {
        Toast(
          "error",
          "There was an error loading the users. If the issue persists please contact the administrators"
        );
      },
    }
  );

  useEffect(() => {
    if (data?.getCompanyAmenitiesWithPagination?.amenities) {
      setAmenities(data?.getCompanyAmenitiesWithPagination?.amenities);
      setPagination((prev) => ({
        ...prev,
        totalCount: data?.getCompanyAmenitiesWithPagination?.totalCount,
      }));
    }
  }, [data]);

  const [addAmenity, { loading: addAmenityLoading }] = useMutation(
    ADD_AMENITY,
    {
      fetchPolicy: "no-cache",
      onCompleted(result) {
        setAmenities((prev) => [...prev, result.addAmenity]);
        toast("success", `Successfully saved ${result.addAmenity?.title}`);
        handleCancel();
      },
      onError(e) {
        toast("error", e?.message ?? "Something went wrong!");
      },
    }
  );

  const [updateAmenity, { loading: editAmenityLoading }] = useMutation(
    UPDATE_AMENITY,
    {
      fetchPolicy: "no-cache",
      onCompleted(result) {
        toast("success", `Successfully saved ${result.updateAmenity?.title}`);
        handleCancel();
      },
      onError(e) {
        toast("error", e?.message ?? "Something went wrong!");
      },
    }
  );

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const onChange = (pagination) => {
    setPagination((prev) => ({
      ...prev,
      page: pagination?.current,
      limit: pagination?.pageSize,
    }));
  };

  const handleDelete = async (amenity) => {
    try {
      await updateAmenity({
        variables: {
          amenityInput: {
            _id: amenity._id,
            company: companyId,
            title: amenity.title,
            isActive: false,
          },
        },
      });

      setAmenities((prev) => prev.filter((item) => item._id !== amenity._id));
    } catch (error) {
      console.log("error in amenities/handleDelete");
    }
  };

  const columns = [
    {
      title: "Office",
      dataIndex: "title",
      key: "title",
      render: (text) => <p>{text}</p>,
    },
    // {
    //   title: "Active",
    //   dataIndex: "isActive",
    //   key: "isActive",
    //   render: (_, record) => checkIsActive(record),
    // },

    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <div className="table-action-btns">
          <Button
            className="border-primary text-primary"
            icon={<EditOutlined />}
            onClick={() => {
              setSelectedAmenity(record.title);
              setSelectAmenityId(record._id);
              showModal();
              setViewMode("Edit");
            }}
          />

          <Popconfirm
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            okType="danger"
            title="Are you sure to Delete?"
          >
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (error) toast("error", error.message);
  }, [error, toast]);

  const firstLoad = loading && !amenities.length;

  return (
    <div>
      <Breadcrumb pageName="Amenities" />
      <div className="m-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <span className="font-bold text-lg">
              Amenities ({pagination.totalCount || 0})
            </span>
            <div className="relative">
              <div className="absolute top-1/2 left-1 -translate-y-1/2">
                <svg
                  className="fill-gray-400"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9.16666 3.33332C5.945 3.33332 3.33332 5.945 3.33332 9.16666C3.33332 12.3883 5.945 15 9.16666 15C12.3883 15 15 12.3883 15 9.16666C15 5.945 12.3883 3.33332 9.16666 3.33332ZM1.66666 9.16666C1.66666 5.02452 5.02452 1.66666 9.16666 1.66666C13.3088 1.66666 16.6667 5.02452 16.6667 9.16666C16.6667 13.3088 13.3088 16.6667 9.16666 16.6667C5.02452 16.6667 1.66666 13.3088 1.66666 9.16666Z"
                    fill=""
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M13.2857 13.2857C13.6112 12.9603 14.1388 12.9603 14.4642 13.2857L18.0892 16.9107C18.4147 17.2362 18.4147 17.7638 18.0892 18.0892C17.7638 18.4147 17.2362 18.4147 16.9107 18.0892L13.2857 14.4642C12.9603 14.1388 12.9603 13.6112 13.2857 13.2857Z"
                    fill=""
                  />
                </svg>
              </div>

              <input
                type="text"
                value={searchText}
                onChange={handleSearch}
                placeholder="Type to search..."
                className="w-full rounded-md border border-stroke bg-transparent py-3 pl-6 pr-10 outline-none focus:border-primary focus-visible:shadow-none "
              />
            </div>
          </div>
          <Button
            className="border-primary text-primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setSelectedAmenity("");
              setSelectAmenityId(null);
              showModal();
              setViewMode("Add");
            }}
          >
            Add New
          </Button>
        </div>
      </div>
      <div className="py-6">
        {error && !amenities.length ? (
          <ErrorState onRetry={() => refetch()} />
        ) : (
          <>
            {error && <ErrorBanner onRetry={() => refetch()} />}
            <Table
              bordered
              rowKey={(record) => record._id}
              onChange={onChange}
              {...(firstLoad
                ? skeletonTableProps(columns)
                : {
                    columns,
                    dataSource: amenities,
                    pagination: {
                      position: ["bottomCenter"],
                      current: pagination.page,
                      pageSize: pagination.limit,
                      total: pagination.totalCount,
                    },
                  })}
              locale={{
                emptyText: searchText ? (
                  <EmptyState
                    title="No amenities match your search"
                    description={`Nothing found for "${searchText}".`}
                    actionLabel="Clear search"
                    onAction={() => setSearchText("")}
                  />
                ) : (
                  <EmptyState
                    title="No amenities yet"
                    description="Amenities you add will be selectable on listings."
                  />
                ),
              }}
            />
          </>
        )}
      </div>
      <Modal
        open={open}
        title={`Amenity`}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={(_, { OkBtn, CancelBtn }) => (
          <>
            <Button
              type="primary"
              className="border-primary text-primary"
              disabled={
                addAmenityLoading ? true : editAmenityLoading ? true : false
              }
              onClick={async () => {
                if (!selectedAmenity.trim()) return;

                if (viewMode === "Add") {
                  addAmenity({
                    variables: {
                      amenityInput: {
                        company: companyId,
                        title: selectedAmenity,
                        isActive: true,
                      },
                    },
                  });
                } else {
                  await updateAmenity({
                    variables: {
                      amenityInput: {
                        _id: selectAmenityId,
                        company: companyId,
                        title: selectedAmenity,
                        isActive: true,
                      },
                    },
                  });

                  setAmenities((prev) =>
                    prev.map((item) => {
                      if (item._id !== selectAmenityId) return item;
                      return { ...item, title: selectedAmenity };
                    })
                  );
                }
              }}
            >
              Save
            </Button>
            <Button key="back" onClick={handleCancel}>
              Cancel
            </Button>
          </>
        )}
      >
        <Input
          value={selectedAmenity}
          placeholder="Enter New Amenity"
          onChange={(event) => {
            setSelectedAmenity(event.target.value);
          }}
          maxLength={40}
        />
      </Modal>
    </div>
  );
};

export default Amenities;
